import { Menu } from "@base-ui-components/react/menu";
import {
  Bell,
  BellOff,
  CirclePause,
  MoreHorizontal,
  QrCode,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion
} from "lucide-react";
import type { LinkView } from "../api";

interface LinkRowProps {
  link: LinkView;
  onQr: (link: LinkView) => void;
  onEdit: (link: LinkView) => void;
  onRun: (link: LinkView) => void;
  onDelete: (link: LinkView) => void;
}

type RowTone = "green" | "yellow" | "red" | "neutral";

export function LinkRow({ link, onQr, onEdit, onRun, onDelete }: LinkRowProps) {
  const status = getStatus(link);
  const failedCount = link.lastFailedCount ?? 0;
  const checkedCount = link.lastCheckedCount ?? 0;
  const onlineCount = link.lastOnlineCount ?? 0;
  const skippedCount = link.lastSkippedCount ?? 0;
  const notificationLabel = failedCount > 0 ? `${failedCount} failed` : "Quiet";

  return (
    <article className="grid gap-4 rounded-lg border border-[#24201b] bg-[#08090b] px-4 py-4 shadow-[0_18px_48px_rgba(0,0,0,0.24)] lg:grid-cols-[minmax(12rem,1fr)_16rem_9rem_9.5rem_5.5rem] lg:items-center lg:py-3">
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-3">
          <span className={`status-icon ${status.iconClass}`} aria-hidden="true">
            <status.Icon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 lg:flex-nowrap">
              <h2 className="min-w-0 break-words text-base font-medium text-white lg:truncate">{link.name}</h2>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${status.badgeClass}`}>{status.label}</span>
              {link.checkBackend === "local_agent_tcp" ? (
                <span className="shrink-0 rounded-full bg-[#182030] px-2 py-0.5 text-xs font-medium text-[#8fbfff] ring-1 ring-[#2c405e]">
                  Local Agent
                </span>
              ) : null}
            </div>
            <p className="mt-1 break-all text-sm leading-5 text-[#9a928a] lg:truncate lg:break-normal">{link.originalUrl}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-sm lg:grid-cols-[4.5rem_4.5rem_4.5rem] lg:items-center lg:gap-3">
        <Metric label="Checked" value={checkedCount} />
        <Metric label="Online" value={onlineCount} tone="green" />
        <Metric label="Failed" value={failedCount} tone={failedCount > 0 ? "red" : "neutral"} />
      </div>

      <div className="flex min-w-0 items-center gap-2 text-sm text-[#a39a91] lg:whitespace-nowrap">
        {failedCount > 0 ? (
          <Bell className="h-4 w-4 text-[#ff7b72]" aria-hidden="true" />
        ) : (
          <BellOff className="h-4 w-4 text-[#6f777d]" aria-hidden="true" />
        )}
        <span>{notificationLabel}</span>
        {skippedCount > 0 ? <span className="text-[#ffd16a]">{skippedCount} skipped</span> : null}
      </div>

      <div className="text-left text-sm text-[#8f877f] lg:whitespace-nowrap">
        <span className="block text-xs uppercase tracking-[0.12em] text-[#5f5750]">Last checked</span>
        {link.lastCheckedAt ? (
          <time className="mt-1 block text-[#d6cec5]" dateTime={link.lastCheckedAt}>
            {formatTimestamp(link.lastCheckedAt)}
          </time>
        ) : (
          <span className="mt-1 block text-[#d6cec5]">Not checked</span>
        )}
      </div>

      <div className="flex items-center gap-2 lg:justify-end">
        <button
          className="icon-button"
          type="button"
          aria-label={`Show QR code for ${link.name}`}
          title="Show QR code"
          onClick={() => onQr(link)}
        >
          <QrCode aria-hidden="true" className="h-5 w-5" />
        </button>
        <Menu.Root>
          <Menu.Trigger className="menu-trigger" aria-label={`Open actions for ${link.name}`} title="Actions">
            <MoreHorizontal aria-hidden="true" className="h-5 w-5" />
          </Menu.Trigger>
          <Menu.Portal>
            <Menu.Positioner side="bottom" align="end" sideOffset={6}>
              <Menu.Popup className="row-menu">
                <Menu.Item className="row-menu-item" onClick={() => onEdit(link)}>
                  Edit
                </Menu.Item>
                <Menu.Item className="row-menu-item" onClick={() => onRun(link)}>
                  Run check
                </Menu.Item>
                <Menu.Item className="row-menu-item row-menu-item-danger" onClick={() => onDelete(link)}>
                  Delete
                </Menu.Item>
              </Menu.Popup>
            </Menu.Positioner>
          </Menu.Portal>
        </Menu.Root>
      </div>
    </article>
  );
}

function Metric({ label, value, tone = "neutral" }: { label: string; value: number; tone?: RowTone }) {
  return (
    <div className="min-w-[4.25rem]">
      <span className="block text-xs uppercase tracking-[0.12em] text-[#5f5750]">{label}</span>
      <span className={`mt-1 block font-semibold ${metricClass(tone)}`}>{value}</span>
    </div>
  );
}

function getStatus(link: LinkView): {
  label: string;
  Icon: typeof ShieldCheck;
  iconClass: string;
  badgeClass: string;
} {
  if (!link.enabled) {
    return {
      label: "Paused",
      Icon: CirclePause,
      iconClass: "border-[#3a352f] bg-[#151515] text-[#8b8580]",
      badgeClass: "bg-[#1a1a1a] text-[#9f9790] ring-1 ring-[#34302b]"
    };
  }

  if (link.lastStatus === "online" && (link.lastFailedCount ?? 0) === 0) {
    return {
      label: "Online",
      Icon: ShieldCheck,
      iconClass: "border-[#1d5636] bg-[#0b2418] text-[#52e391]",
      badgeClass: "bg-[#0d2b1b] text-[#52e391] ring-1 ring-[#1d5636]"
    };
  }

  if (link.lastStatus === "offline") {
    return {
      label: "Offline",
      Icon: ShieldAlert,
      iconClass: "border-[#67302c] bg-[#2b1010] text-[#ff7b72]",
      badgeClass: "bg-[#301211] text-[#ff7b72] ring-1 ring-[#67302c]"
    };
  }

  if (link.lastStatus === "degraded" || (link.lastFailedCount ?? 0) > 0) {
    return {
      label: "Degraded",
      Icon: ShieldAlert,
      iconClass: "border-[#684b1f] bg-[#2d210f] text-[#ffd16a]",
      badgeClass: "bg-[#30230f] text-[#ffd16a] ring-1 ring-[#684b1f]"
    };
  }

  return {
    label: "Not checked",
    Icon: ShieldQuestion,
    iconClass: "border-[#3a352f] bg-[#151515] text-[#8b8580]",
    badgeClass: "bg-[#1a1a1a] text-[#9f9790] ring-1 ring-[#34302b]"
  };
}

function metricClass(tone: RowTone): string {
  const classes: Record<RowTone, string> = {
    green: "text-[#52e391]",
    yellow: "text-[#ffd16a]",
    red: "text-[#ff7b72]",
    neutral: "text-[#d6cec5]"
  };
  return classes[tone];
}

function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Invalid date";

  return new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Shanghai",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(date);
}
